import React, { ReactElement } from 'react'
import styles from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedStyling.module.scss'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Fall2025: React.FC = (): ReactElement => {
    return (
        <div className={styles.examChangeGuideSharedComponent} id={'fall2025'}>
            <div className={styles.examChangeGuidePageTop}>
                <h2>Fall 2025</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has now released the significant changes effective
                    for the Fall 2025 sitting of FSA Group and Health Exams. The
                    public announcement can be found here:&nbsp;
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            margin: '1rem',
                        }}
                    >
                        <a
                            href={
                                'https://fsa2025.soa.org/?utm_campaign=2025-05-20_FSA%20Redesign_USA_Non-Members%20Communication_MP&utm_medium=email&utm_source=Eloqua'
                            }
                            target={'_blank'}
                            rel={'noreferrer'}
                            className={reusableCssClass.clickableText}
                        >
                            Evolving the FSA Pathway
                        </a>
                    </div>
                    <div>
                        This page will not attempt to summarize or duplicate the
                        entire announcement, but instead will focus on the
                        changes between the previous set of exams and the new
                        ones.
                    </div>
                    <br />
                    <div>
                        The intended reader is someone who has been taking FSA
                        health exams before the transition and intends to
                        continue taking FSA health exams after the transition.
                    </div>
                    <br />
                    <div>
                        There were previously 3 health-related exams. Now, there
                        are 4.
                    </div>
                    <br />
                    <div>
                        The syllabi for the new health exams can be found here:
                    </div>
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2025/fall/syllabi/2025-11-gh-101-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH101: Benefits and Pricing
                            </a>
                            &nbsp;(previously called &quot;Design and
                            Pricing&quot;, with a few elements from &quot;Risk
                            Mitigation&quot;)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2025/fall/syllabi/2025-11-gh-201u-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH201: Valuation and Regulation, US
                            </a>
                            &nbsp;(previously called by the same name, with a
                            few elements from &quot;Design and Pricing&quot;)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2025/fall/syllabi/2025-11-gh-301-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH301: Health Analytics and Management
                            </a>
                            &nbsp;(previously called &quot;Risk
                            Mitigation&quot;, with a few brand new sections)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2025/fall/syllabi/2025-11-cp-321-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                CP321: Disability, Long-Term Care, and
                                Long-Duration Health Contracts
                            </a>
                            &nbsp;(primarily made up of pieces of &quot;Design
                            and Pricing&quot; and &quot;Valuation and
                            Regulation&quot;, and a section from Life and
                            Retiree Group Benefits)
                        </li>
                    </ul>
                    <div>
                        Two new additions on top of the syllabi typically
                        provided by the SoA is a course strategy guide and
                        introductory study note.
                    </div>
                    <br />
                    <div>
                        Links to all three of these will be provided on the
                        left-hand side of the page when you are logged in for
                        easy access.
                    </div>
                    <br />
                    The course strategy guides serve as a roadmap for{' '}
                    <b>what</b> to learn, <b>why</b> it matters, and <b>how</b>{' '}
                    to study it effectively so you can tackle the assessment
                    with confidence. More specifically, it provides the
                    following:
                    <ul>
                        <li>
                            <div>
                                <b>Course Overview & Objectives</b>
                            </div>
                            <div>
                                A one-page summary telling you what the course
                                is about, why it exists (its role in the broader
                                SOA curriculum), and how it builds on or feeds
                                into the other Group & Health (GH) or
                                cross-practice (CP) courses.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Recommended Study Approach</b>
                            </div>
                            <div>
                                A step-by-step plan for how to tackle the
                                materials: read the Guide, scan the learning
                                objectives/outcomes, watch any linked videos,
                                then work through the syllabus readings in a
                                prescribed order—highlighting what to skim on
                                first pass versus drill into later.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Syllabus Topics & Learning Outcomes</b>
                            </div>
                            <div>
                                A clear listing of the major “buckets” of
                                content (e.g. Reserving, Financial Statements,
                                Risk Adjustment, Plan Provisions, etc.), often
                                broken into five or six topics, each with
                                descriptions of what you’re expected to learn.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Reading List & Sequence</b>
                            </div>
                            <div>
                                For each topic you get a curated sequence of
                                readings (textbook chapters, SOA study notes,
                                professional articles, ASOPs/standards) flagged
                                with “first read,” “second read,” “skim now,
                                study later,” etc.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Assessment Guidance</b>
                            </div>
                            <div>
                                Advice on exam format (multi-part,
                                scenario-based questions), the emphasis on
                                critical thinking over rote answers, and tips on
                                how to apply what you’ve learned as opposed to
                                simply recalling details.
                            </div>
                        </li>
                    </ul>
                    The introductory study note isn&apos;t content to learn -
                    it&apos;s your one-stop shop for <b>what</b> exam
                    you&apos;re taking, <b>how</b> it&apos;s structured,{' '}
                    <b>what</b> materials count, <b>where</b> to find updates,
                    and <b>which</b> study notes you&apos;ll need. More
                    specifically, it provides the following:
                    <ul>
                        <li>
                            <div>
                                <b>Exam Logistics & Format</b>
                            </div>
                            <div>
                                They open with the exam date, duration (three
                                hours), format (written-answer, 50 points), and
                                location scheduling.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Curriculum & Sequence Policy</b>
                            </div>
                            <div>
                                A reminder that you can take any four fellowship
                                courses in any order, but you must include one
                                100-level/200-level pairing in your four.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Syllabus & Materials</b>
                            </div>
                            <div>
                                Clarification of what “syllabus materials” are
                                (textbooks, readings, videos, strategy guide,
                                study notes), where to find the full syllabus
                                and Learning Objectives, how updates or errata
                                will be communicated, and where past exams live.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Study Aids & Guidance</b>
                            </div>
                            <div>
                                Pointers to the Course Strategy Guide, the SOA’s
                                Guide to Written Exams for cognitive verbs,
                                seminars/workshops and study manuals (with a
                                disclaimer that they’re not official SOA
                                content).
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Exam Day Rules & Calculator Policy</b>
                            </div>
                            <div>
                                The strict list of approved TI calculator models
                                (with memory-clearing rules), plus
                                disqualification for non-approved devices.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Study Notes Appendix</b>
                            </div>
                            <div>
                                A table of the course’s SOA-published study
                                notes (with former codes, and “*” marking which
                                notes are in the revision set), along with any
                                errata notices.
                            </div>
                        </li>
                        <li>
                            <div>
                                <b>Disclaimers & Acknowledgments</b>
                            </div>
                            <div>
                                Statement that study notes aren’t SOA’s official
                                opinions, thanks to authors, and notice of joint
                                sponsorship by actuarial bodies.
                            </div>
                        </li>
                    </ul>
                    In terms of how the exams have changed from their
                    predecessors, find a summary below:
                    <br />
                    <br />
                    GH101 has the following changes compared to its predecessor
                    GHDP:
                    <ul>
                        <li>
                            The previous sections, &quot;Plan Provisions and
                            Government Programs&quot;, &quot;Manual Rates&quot;,
                            and &quot;Underwriting&quot; remain, but have been
                            significantly slimmed down. Specifically, the
                            &quot;Government Programs&quot; section was removed
                            (and transferred to GH201).
                        </li>
                        <li>
                            A significant portion of the &quot;Employee Benefit
                            Strategy&quot; section from GHRM was added to the
                            this exam&apos;s syllabus.
                        </li>
                        <li>
                            There are a handful of videos, a form of content
                            which has not been present in the previous syllabi
                            before.
                        </li>
                    </ul>
                    GH201U has the following changes compared to its predecessor
                    GHVRU:
                    <ul>
                        <li>
                            The previous sections, &quot;Reserving&quot;,
                            &quot;Financial Statements&quot;, and
                            &quot;Regulation&quot; remain, but have been
                            significantly slimmed down.
                        </li>
                        <li>
                            A section titled &quot;Government Programs&quot; was
                            added, which came from the previous GHDP syllabus.
                        </li>
                        <li>
                            A small section titled &quot;Flow of Funds&quot; was
                            added.
                        </li>
                        <li>
                            There are a handful of videos, a form of content
                            which has not been present in the previous syllabi
                            before.
                        </li>
                    </ul>
                    GH301 has the following changes compared to its predecessor
                    GHRM:
                    <ul>
                        <li>
                            The previous sections, &quot;Provider
                            Reimbursement&quot;, &quot;Claims and Disease
                            Management&quot;, and &quot;Risk Adjustment&quot;
                            remain, but have been significantly slimmed down.
                        </li>
                        <li>
                            The &quot;Employee Benefit Strategy&quot; section
                            was removed (and transferred to GH101).
                        </li>
                        <li>
                            Two small sections, titled &quot;Medical Data&quot;
                            and &quot;Predictive Analytics&quot;, were added,
                            which are both eLearning Modules, a form of content
                            which has not been present in the previous syllabi
                            before.
                        </li>
                        <li>
                            A small section titled &quot;Social Determinants of
                            Health&quot; was added.
                        </li>
                    </ul>
                    CP321 is composed of the following:
                    <ul>
                        <li>
                            <b>Plan and Product Provisions</b> - this section
                            comes from items that were removed from the previous
                            GHDP syllabus.
                        </li>
                        <li>
                            <b>Manual Rates</b> - this section comes from items
                            that were removed from the previous GHDP syllabus
                            (both &quot;Manual Rates&quot; and
                            &quot;Underwriting&quot;).
                        </li>
                        <li>
                            <b>Reserving</b> - this section comes from items
                            that were removed from the previous GHVRU syllabus.
                        </li>
                        <li>
                            <b>Financial Statements</b> - this section comes
                            from items that were removed from the previous GHVRU
                            syllabus.
                        </li>
                        <li>
                            <b>Asset Adequacy</b> - this is a small section
                            composed of two study notes and an ASOP.
                        </li>
                        <li>
                            <b>Retiree Group Benefits</b> - this is a fairly
                            large section composed of several new study notes
                            and a few websites.
                        </li>
                    </ul>
                    <div>
                        If you have any questions about the transition, do not
                        hesitate to reach out to us at
                        support@justenoughprep.com, and we will be happy to help
                        assist.
                    </div>
                </div>
            </div>
        </div>
    )
}
